import { get } from 'lodash';
import { ComponentDescriptor } from '../../../../components/PageBuilder';
import {
  Condition,
  DependsOnFn,
  FormField,
  SimplifiedCondition,
  SimplifiedConditions,
  ValidatorDescriptor,
  Validators,
} from '../../../../state-machines/form-machine';
import { Step } from '../../../../state-machines/wizard-machine';
import {
  fieldMapping,
  propMapping,
  validatorsMapping,
  transformersMapping,
  componentsMapping,
  inputsMapping,
  excludeDependency,
  inputsAdditionalProps,
} from './mappings';

export interface LegacyPartnerValidation {
  type: string; // function name
  value: string | number; // param
}

export interface Partner {
  type: string;
  fieldName: string;
  required: boolean;
  values: Values[] | Values;
  validations: LegacyPartnerValidation[];
  mapping?: string;
  props?: any; // props passed to the component
}

export interface Values {
  [actualValue: string]: string; // <- the value is the label -_-
}

type LegacyFieldValidationParams = {
  errorMessage?: string;
  params?: string | number | {};
};
export interface LegacyFieldValidation {
  [validatorName: string]: LegacyFieldValidation | LegacyFieldValidationParams;
}

export interface LegacyConditional {
  type?: 'match' | 'validation' | 'and' | 'nor' | 'or';
  show?: boolean;
  field?: any;
  match?: string;
  validation?: LegacyFieldValidation;
  value?: any;
  matchOne?: string[];
}

export interface PropMap {
  [propName: string]: string; // path to some global data store I guess
}
export type OrNull<T> = T | null;
type NullableProps = Omit<LegacyComponentDefinitionBase, 'name' | 'label'>;
export type LegacyComponentDefinition = {
  [K in keyof NullableProps]: OrNull<NullableProps[K]>;
} & Pick<LegacyComponentDefinitionBase, 'name' | 'label'>;
export interface LegacyComponentDefinitionBase {
  position: number;
  type?: string;
  name: string;
  value?: string;
  label?: string;
  values?: Values;
  mapping?: string;
  hidden?: boolean;
  partner?: Partner;
  row?: number | string;
  size?: number;
  normalize?: string; // transformer name
  validation?: LegacyFieldValidation;
  asyncValidation?: LegacyFieldValidation;
  input?: boolean; // so what are all the ones without input = true ?
  conditional?: LegacyConditional; // somehow works with mapping to determine if should be shown or not
  component?: string; // maps to a component in the lib I guess
  props?: any; // props passed to the component
  propMap?: PropMap;
  frontMapping?: string; // sure why not after all...
  documentation?: string; // lol
  disabled?: boolean | 'disabled'; // defaults to false I guess
  rowClass?: string; // ...
  placeholder?: string; // ...
  maxlength?: number;
  __typename?: string;
}

export interface LegacyStepDefinition {
  position: number;
  title: string;
  components: LegacyComponentDefinition[];
}

export interface LegacyOrderDefinition {
  steps: LegacyStepDefinition[];
}

export interface LegacyOrderResponse {
  data: LegacyOrderDefinition[];
  meta: {
    count: number;
    apiUser: string;
    cascade: boolean;
    category: string; // example: energy
    input: { providerId: number; productId: string };
  };
  statuscode: number;
  message: string;
}

const sortParamsAsLastElement = (a: string, b: string) => {
  if (a === 'params') return 1;
  if (b === 'params') return -1;
  else return 0;
};

const parseNestedValidator = <
  LV extends LegacyFieldValidation,
  N extends keyof typeof validatorsMapping,
  K extends string[],
>(
  legacyValidation: LV,
  validatorName: N,
  validatorKeys: K
) => {
  const fnName = validatorsMapping[validatorName];
  let args: ValidatorDescriptor['args'] = [];
  const errMsgs = {};
  for (const errMsgName of validatorKeys) {
    if (errMsgName !== 'params')
      errMsgs[errMsgName] =
        legacyValidation[validatorName][errMsgName].errorMessage;
  }
  args.push(errMsgs);
  if (legacyValidation[validatorName].params !== undefined)
    args.push(legacyValidation[validatorName].params);
  return { fnName, args };
};

//should probably make that function a recursive one instead (idk how nested validators can be in legacy)
const parseValidators = (
  legacyValidation: LegacyFieldValidation | undefined | null
): Validators | undefined => {
  if (!legacyValidation) return undefined;
  const validators: Validators = [];
  for (const validatorName in legacyValidation) {
    let fnName: string | undefined;
    let args: ValidatorDescriptor['args'] = [];
    const validatorKeys = Object.keys(legacyValidation[validatorName]).sort(
      sortParamsAsLastElement
    );
    if (validatorKeys.includes('errorMessage')) {
      fnName = validatorsMapping[validatorName];
      args.push(legacyValidation[validatorName].errorMessage);
      if (legacyValidation[validatorName].params !== undefined)
        args.push(legacyValidation[validatorName].params);
    } else {
      const validator = parseNestedValidator(
        legacyValidation,
        validatorName as keyof typeof validatorsMapping,
        validatorKeys
      );
      fnName = validator.fnName;
      args = validator.args;
    }
    if (fnName) validators.push({ fn: fnName, args });
  }
  return validators.length > 0 ? validators : undefined;
};

const parseOptions = (values) => {
  if (!values) return undefined;
  const options: FormField['options'] = [];
  for (const value in values) {
    if (typeof values[value] === 'string') {
      const label = values[value];
      options.push({ label, value });
    } else {
      options.push({ label: values[value].title, value });
    }
  }
  return options;
};

const parseExtraOptions = (values) => {
  if (!values) return undefined;
  const options: any[] = [];
  for (const value in values) {
    if (typeof values[value] === 'string') {
      const label = values[value];
      options.push({ title: label, value });
    } else {
      options.push({ ...values[value], value });
    }
  }

  return options;
};

const extraOptionsTypes = ['checkbox_group', 'radio_price', 'select_price'];

export const parseMatchConditional = (
  legacyCondition: LegacyConditional,
  stepName: string,
  legacyStep: LegacyStepDefinition,
  extraFields: FormField[] = [],
  productType: string
): Condition | undefined => {
  const { value, match, matchOne, field, type } = legacyCondition;
  let name: string | undefined;
  let condition: SimplifiedCondition | undefined;

  if (
    (match !== undefined || matchOne !== undefined || type === 'match') &&
    field
  ) {
    name = field;
  }

  if (name) {
    const isFromSameStep = legacyStep.components.find((c) => c.name === name);
    const isFromExtraFields = extraFields.find((f) => f.name === name);

    if (matchOne !== undefined) {
      condition = {
        name:
          (isFromSameStep || isFromExtraFields ? '' : stepName + '.') + name,
        condition: {
          fn: 'isOneOf',
          args: [matchOne],
        },
      };
    } else {
      const val = match !== undefined && match !== null ? match : value;
      condition = {
        name:
          (isFromSameStep || isFromExtraFields ? '' : stepName + '.') + name,
        condition: {
          fn: 'isEqual',
          args: [Array.isArray(val) && val.length === 0 ? 0 : val],
        },
      };

      // the following hardcoded part helps bypass a BE issue with CB
      // membership fields on 3P.
      if (productType === 'triplePlay' && condition.name.includes('isMember')) {
        if (val === 'false') {
          condition = {
            name: 'isMember',
            condition: {
              fn: 'isEqual',
              args: [0],
            },
          };
        } else {
          condition = {
            name: 'is_cb_member',
            condition: {
              fn: 'isEqual',
              args: [val],
            },
          };
        }
      }
    }
  }
  return condition;
};

const parseValidatorConditional = (
  conditionsAndEffect: SimplifiedConditions,
  stepName: string,
  legacyStep: LegacyStepDefinition,
  legacyCondition: LegacyConditional
) => {
  const { type, value, field } = legacyCondition;
  if (type === 'validation' && (field || value)) {
    const validatorCondition = parseValidators(
      (value as LegacyConditional).validation as LegacyFieldValidation
    );

    if (validatorCondition) {
      const fieldName = field ?? value;

      const isFromSameStep = legacyStep.components.find(
        (c) => c.name === fieldName?.field
      );

      conditionsAndEffect.conditions.push({
        name: (isFromSameStep ? '' : stepName + '.') + (field ?? value?.field),
        condition: validatorCondition,
      });
    }
  }
  return conditionsAndEffect;
};

export const parseOrCondition = (value: LegacyConditional[]): DependsOnFn => {
  return (field, otherFields) => {
    const isActive = otherFields.some((f) => {
      if (f.value === undefined) return false;

      return value.some((val) => {
        if (val.matchOne) {
          const valueAsString =
            typeof f.value === 'number'
              ? f.value.toString()
              : (f.value as string);
          return (
            val.field === f.name &&
            val.matchOne.includes(valueAsString) &&
            f.active
          );
        }
        return val.field === f.name && val.match === f.value && f.active;
      });
    });
    return {
      ...field,
      active: isActive,
    };
  };
};

export const parseConditional = (
  legacyCondition: LegacyConditional,
  stepName: string,
  legacyStep: LegacyStepDefinition,
  extraFields: FormField[],
  productType: string
): SimplifiedConditions | DependsOnFn => {
  const { type, value, match, matchOne } = legacyCondition;
  let conditionsAndEffect: SimplifiedConditions = { conditions: [] };

  if (type === 'or') {
    return parseOrCondition(value as LegacyConditional[]);
  }

  if ((type === 'and' || type === 'nor') && Array.isArray(value)) {
    for (let condition of value) {
      const composedCondition = parseConditional(
        condition,
        stepName,
        legacyStep,
        extraFields,
        productType
      );
      if (composedCondition && typeof composedCondition !== 'function') {
        conditionsAndEffect.conditions = [
          ...conditionsAndEffect.conditions,
          ...composedCondition.conditions,
        ];
      }
    }
  }

  conditionsAndEffect = parseValidatorConditional(
    conditionsAndEffect,
    stepName,
    legacyStep,
    legacyCondition
  );

  if (type === 'match' || match !== undefined || matchOne !== undefined) {
    const matchCondition = parseMatchConditional(
      legacyCondition,
      stepName,
      legacyStep,
      extraFields,
      productType
    );
    if (matchCondition) conditionsAndEffect.conditions.push(matchCondition);
  }

  conditionsAndEffect.active = true;

  return conditionsAndEffect;
};

const getFieldDependencies = (
  stepName: string,
  legacyComponent: LegacyComponentDefinition,
  legacyStep: LegacyStepDefinition,
  extraFields: FormField[],
  productType: string
) => {
  const { conditional } = legacyComponent;

  let dependsOn: FormField['dependsOn'] = conditional
    ? parseConditional(
        conditional,
        stepName,
        legacyStep,
        extraFields,
        productType
      )
    : undefined;

  if (dependsOn && typeof dependsOn !== 'function') {
    const oppositeMatchConditions = (
      dependsOn as SimplifiedConditions
    ).conditions
      .map((c) => {
        if (c.condition && typeof c.condition === 'object') {
          const condition = c.condition as ValidatorDescriptor;
          if (condition.fn === 'isEqual') {
            return {
              ...c,
              condition: { ...condition, fn: 'isNotEqual' },
              active: false,
            };
          }
          if (condition.fn === 'isOneOf') {
            return {
              ...c,
              condition: { ...condition, fn: 'isNotOneOf' },
              active: false,
            };
          }
        }
        return undefined;
      })
      .filter((c: Condition | undefined): c is Condition => !!c) as Condition[];

    if (oppositeMatchConditions.length > 0) {
      dependsOn = [dependsOn, ...oppositeMatchConditions];
    }
  }

  return dependsOn;
};

const normalizersFromTypes: OrNull<string | undefined>[] = ['iban', 'phone'];

const getAdditionalInputsProps = (inputType, comparatorType) => {
  if (!inputType || !comparatorType) return {};
  if (comparatorType.includes('TriplePlay')) {
    return inputsAdditionalProps[inputType] || {};
  } else return {};
};

export const parseLegacyComponentToFormField = (
  legacyComponent: LegacyComponentDefinition,
  stepName: string,
  legacyStep: LegacyStepDefinition,
  extraFields: FormField[],
  productType
): FormField | undefined => {
  const {
    name,
    disabled,
    hidden,
    value,
    type,
    label,
    normalize,
    component,
    validation,
    values,
    props,
    __typename,
    propMap,
    partner,
    input,
  } = legacyComponent;

  if (!component && !type) return undefined;

  const componentType = type ? inputsMapping[type] : undefined;

  const propsFromMapping = getAdditionalInputsProps(type, __typename);

  const normalizeFromType = normalizersFromTypes.includes(type)
    ? type
    : normalize;

  let dependsOn: FormField['dependsOn'] = excludeDependency(name, __typename)
    ? undefined
    : getFieldDependencies(
        stepName,
        legacyComponent,
        legacyStep,
        extraFields,
        productType
      );

  let transformers = normalizeFromType
    ? transformersMapping[normalizeFromType]
    : [];

  transformers =
    type === 'date'
      ? [...transformersMapping[type], ...transformers]
      : transformers;

  const field: FormField = {
    active: disabled !== false && input !== false,
    name: name,
    value: value === null ? undefined : (value as FormField['value']),
    type: componentType,
    label: label,
    transformers,
    validator:
      type === 'phone' || type === 'date'
        ? [
            validatorsMapping[type],
            ...((parseValidators(validation) as []) ?? []),
          ]
        : parseValidators(validation),
    options: extraOptionsTypes.includes(type as string)
      ? parseExtraOptions(values)
      : parseOptions(values),
    dependsOn,
    meta: type
      ? {
          partner,
          props: {
            _tooltip: props?.labelTooltip,
            type: type !== 'date' ? type : undefined,
            hidden,
            disabled,
            ...props,
            ...propsFromMapping,
          },
        }
      : undefined,
    propMap,
  };
  return field;
};

export const parseLegacyComponentToNewComponent = (
  legacyComponent: LegacyComponentDefinition,
  appSteps: Step[],
  stepName: string
): ComponentDescriptor | undefined => {
  const {
    name,
    value,
    component,
    label,
    props,
    propMap,
    position = 0,
    partner,
  } = legacyComponent;
  if (!component || !componentsMapping[component]) return undefined;
  let type, propsFromMapping;
  if (typeof componentsMapping[component] === 'string') {
    type = componentsMapping[component];
  } else {
    type = (componentsMapping[component] as ComponentDescriptor).type;
    propsFromMapping = (componentsMapping[component] as ComponentDescriptor)
      .props;
  }
  let descriptor: ComponentDescriptor = {
    type,
    props: {
      ...props,
      name: stepName + '.' + name,
      fieldName: type === 'Field' ? stepName + '.' + name : undefined,
      label,
      value,
      position,
      partner,
      children: propsFromMapping?.valueAsChildren ? value : undefined,
      ...propsFromMapping,
    },
  };
  if (propMap) {
    for (const propName in propMap) {
      const propPath = propMapping[propMap[propName]];
      const fieldValue = findValueInSteps(propPath, appSteps);
      descriptor.props[propName] = fieldValue;
    }
  }
  return descriptor;
};

export const parseLegacyComponents = (
  applyPageSteps: LegacyStepDefinition[],
  appSteps: Step[]
) => {
  const layout: ComponentDescriptor[] = [];
  for (const applyPageStep of applyPageSteps) {
    for (const legacyComponent of applyPageStep.components) {
      const componentDescriptor = parseLegacyComponentToNewComponent(
        legacyComponent,
        appSteps,
        parseLegacyStepTitleToName(applyPageStep.title)
      );
      if (componentDescriptor) layout.push(componentDescriptor);
    }
  }
  return layout;
};

export const parseLegacyStepTitleToName = (title: string) => {
  return title.replace(/ /g, '-').toLowerCase();
};

const parseLegacyStep = (
  legacyStep: LegacyStepDefinition,
  extraFields: FormField[] = [],
  productType
): Step => {
  const step: Step = {
    name: parseLegacyStepTitleToName(legacyStep.title),
    label: legacyStep.title,
    fields: [],
  };

  for (const component of legacyStep.components) {
    const field = parseLegacyComponentToFormField(
      component,
      step.name,
      legacyStep,
      extraFields,
      productType
    );
    if (field) step.fields.push(field);
  }

  return { ...step, fields: [...step.fields, ...extraFields] };
};

export const legacyOrderDefinitionToSteps = (
  orderDef: LegacyOrderDefinition,
  extraFields: FormField[] = [],
  productType
): Step[] => {
  const steps: Step[] = [];
  for (const legacyStep of orderDef.steps) {
    const step = parseLegacyStep(legacyStep, extraFields, productType);
    if (step) steps.push(step);
  }
  return steps;
};

const findValueInSteps = (completeFieldName: string, steps: Step[]) => {
  let fieldValue;
  const fieldNameAsArray = completeFieldName.split('.');
  if (fieldNameAsArray.length > 1) {
    const formName = fieldNameAsArray[0];
    const fieldName = fieldNameAsArray[1];
    const appStep = steps.find((s) => s.name === formName);
    if (appStep) {
      const appField = appStep.fields.find((f) => f.name === fieldName);
      if (appField) {
        fieldValue = appField.value;
        if (
          fieldName === 'selectedProduct' &&
          typeof appField.value === 'string' &&
          fieldNameAsArray.length > 2
        ) {
          const path = fieldNameAsArray.slice(2).join('.');
          const selectedProduct = JSON.parse(appField.value);
          fieldValue = getValueFromObject(selectedProduct, path);
        } else if (
          fieldName === 'selectedProduct' &&
          typeof appField.value === 'object'
        ) {
          const path = fieldNameAsArray.slice(2).join('.');
          fieldValue = getValueFromObject(appField.value, path);
        }
      }
    }
  }
  return fieldValue;
};

const getValueFromObject = (value, path) =>
  path === 'products' ? JSON.stringify(get(value, path)) : get(value, path);

/* Get values from previous step and replace the applyPage steps field values with them */
export const replaceWithPreviousStepsValues = (
  applyPageSteps: Step[],
  appSteps: Step[]
): Step[] => {
  const updatedSteps: Step[] = [...applyPageSteps];
  let stepIndex = 0;
  for (const step of updatedSteps) {
    let fieldIndex = 0;
    for (const field of step.fields) {
      const mappedFieldName = fieldMapping[field.name];
      let fieldValue = field.value;
      if (mappedFieldName) {
        fieldValue = findValueInSteps(mappedFieldName, appSteps);
      }
      updatedSteps[stepIndex].fields[fieldIndex].value = fieldValue;
      fieldIndex++;
    }
    stepIndex++;
  }
  return updatedSteps;
};
